import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {EmblaCarouselDirective} from 'embla-carousel-angular';
import type {EmblaCarouselType} from 'embla-carousel';
import {FeedPost} from "../../_types/content";

@Component({
    selector: 'app-embla-carousel',
    templateUrl: './embla-carousel.component.html',
    styleUrls: ['./embla-carousel.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EmblaCarouselComponent implements OnChanges, AfterViewInit, OnDestroy {
    @Input() images: string[] = [];
    @Input() posts: FeedPost[] = [];

    @ViewChild('dotsNode') dotsNodeRef!: ElementRef<HTMLElement>;
    @ViewChild('embla', {read: EmblaCarouselDirective}) emblaRef!: EmblaCarouselDirective;

    emblaApi: EmblaCarouselType | null = null;
    currentSlide: number = 1;
    totalSlides: number = 0;
    private reinitTimer?: number;
    private dotsEventsBound: boolean = false;

    constructor(private cdr: ChangeDetectorRef) {
    }

    ngAfterViewInit(): void {
        queueMicrotask((): void => {
            this.emblaApi = this.emblaRef?.emblaApi;
            if (this.emblaApi) {
                this.bindDotEventsOnce();
                this.scheduleReinit();
                this.updateCounter();
            }
        });
        setTimeout((): void => {
            this.emblaApi = this.emblaRef?.emblaApi;
            if (this.emblaApi) {
                this.bindDotEventsOnce();
                this.scheduleReinit();
                this.updateCounter();
            }
        }, 500);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['images']) return;
        this.scheduleReinit();
    }

    ngOnDestroy(): void {
        if (this.reinitTimer) window.clearTimeout(this.reinitTimer);
    }

    private scheduleReinit(): void {
        if (!this.emblaApi) return;

        if (this.reinitTimer) window.clearTimeout(this.reinitTimer);

        this.reinitTimer = window.setTimeout((): void => {
            requestAnimationFrame((): void => {
                this.emblaApi?.reInit();
                this.rebuildDots();
                this.updateDots();
                this.updateCounter();
            });
        }, 0);
    }

    private bindDotEventsOnce(): void {
        if (!this.emblaApi || this.dotsEventsBound) return;
        this.dotsEventsBound = true;

        const sync = (): void => {
            this.rebuildDots();
            this.updateDots();
            this.updateCounter();
        };

        this.emblaApi.on('init', sync);
        this.emblaApi.on('reInit', sync);
        this.emblaApi.on('select', sync);
    }

    private updateCounter(): void {
        const embla: EmblaCarouselType | null = this.emblaApi;
        if (!embla) return;

        this.totalSlides = embla.scrollSnapList().length;
        this.currentSlide = embla.selectedScrollSnap() + 1;
        this.cdr.detectChanges();
    }

    private rebuildDots(): void {
        const dotsContainer: HTMLElement = this.dotsNodeRef?.nativeElement;
        const embla: EmblaCarouselType | null = this.emblaApi;
        if (!dotsContainer || !embla) return;

        dotsContainer.innerHTML = '';

        embla.scrollSnapList().forEach((_, index): void => {
            const dot: HTMLButtonElement = document.createElement('button');
            dot.classList.add('emblaDot');
            dot.type = 'button';
            dot.addEventListener('click', (): void => embla.scrollTo(index), false);
            dotsContainer.appendChild(dot);
        });
    }

    private updateDots(): void {
        const dotsContainer: HTMLElement = this.dotsNodeRef?.nativeElement;
        const embla: EmblaCarouselType | null = this.emblaApi;
        if (!dotsContainer || !embla) return;

        const selectedIndex: number = embla.selectedScrollSnap();
        Array.from(dotsContainer.children).forEach((dot, index): void => {
            dot.classList.toggle('emblaDotSelected', index === selectedIndex);
        });
        this.cdr.detectChanges();
    }

}
